import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const dateSortComparator = (v1, v2) => {
  if (!v1 && !v2) return 0;
  if (!v1) return -1;
  if (!v2) return 1;
  return new Date(v1) - new Date(v2);
};

// The raw data table's columns in the order the download has always had them,
// with how each one draws. Their names, labels, descriptions and whether they
// start hidden come from the report field registry
// (GET /organization/report-fields), the same source as the data dictionary:
// see rawDataColumns().
export const RAW_DATA_LAYOUT = [
  {
    field: "specimen_capture_date",
    minWidth: 200,
    sortComparator: dateSortComparator,
    valueFormatter: (value) => (value ? dayjs.utc(value).format("M/D/YYYY") : ""),
  },
  {
    field: "collection_site",
    minWidth: 200,
  },
  {
    field: "collection_site_county",
    minWidth: 200,
  },
  {
    field: "collection_method_display_name",
    minWidth: 200,
  },
  {
    field: "summary_species",
    minWidth: 200,
  },
  {
    field: "algorithmSpecies",
    minWidth: 200,
  },
  {
    field: "unknownNotes",
    minWidth: 200,
  },
  {
    field: "algorithm_genus_front",
    minWidth: 200,
  },
  {
    field: "algorithm_species_front",
    minWidth: 200,
  },
  {
    field: "algorithm_sex_front",
    minWidth: 200,
  },
  {
    field: "algorithm_empty_front",
    minWidth: 200,
  },
  {
    field: "algorithm_confidence_front",
    minWidth: 200,
  },
  {
    field: "algorithm_genus_back",
    minWidth: 200,
  },
  {
    field: "algorithm_species_back",
    minWidth: 200,
  },
  {
    field: "algorithm_sex_back",
    minWidth: 200,
  },
  {
    field: "algorithm_empty_back",
    minWidth: 200,
  },
  {
    field: "algorithm_confidence_back",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_genus",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_species",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_sex",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_confidence",
    minWidth: 200,
  },
  {
    field: "algVectorType",
    minWidth: 200,
  },
  {
    field: "algRegionName",
    minWidth: 200,
  },
  {
    field: "algVersion",
    minWidth: 200,
  },
  {
    field: "algConfidenceThreshold",
    minWidth: 200,
  },
  {
    field: "tray",
    minWidth: 200,
  },
  {
    field: "specimen",
    minWidth: 200,
  },
  {
    field: "institution_id",
    minWidth: 200,
  },
  {
    field: "user",
    minWidth: 200,
  },
  {
    field: "position",
    minWidth: 200,
  },
  {
    field: "device",
    minWidth: 200,
  },
  {
    field: "imaging_date_time",
    minWidth: 200,
    sortComparator: dateSortComparator,
  },
  {
    field: "contributed_data_genus",
    minWidth: 200,
  },
  {
    field: "contributed_data_species",
    minWidth: 200,
  },
  {
    field: "contributed_data_sex",
    minWidth: 200,
  },
  {
    field: "contributed_data_life_stage",
    minWidth: 200,
  },
  {
    field: "contributed_data_gonotrophic",
    minWidth: 200,
  },
  {
    field: "contributed_data_plate",
    minWidth: 200,
  },
  {
    field: "contributed_data_well",
    minWidth: 200,
  },
  {
    field: "notes",
    minWidth: 200,
  },
  {
    field: "tags",
    minWidth: 200,
  },
  {
    field: "specimen_tags",
    minWidth: 200,
  },
  {
    field: "collection_method_vector_type",
    minWidth: 200,
  },
  {
    field: "collection_method_trap_type",
    minWidth: 200,
  },
  {
    field: "collection_method_attractant_type",
    minWidth: 200,
  },
  {
    field: "collection_method_description",
    minWidth: 200,
  },
  {
    field: "collection_site_address",
    minWidth: 200,
  },
  {
    field: "collection_site_latitude",
    minWidth: 200,
  },
  {
    field: "collection_site_longitude",
    minWidth: 200,
  },
  {
    field: "collection_site_description",
    minWidth: 200,
  },
];

// lists (tags) read "a, b" in the grid and in the download alike
const listFormatter = (value) => (Array.isArray(value) ? value.join(", ") : value);

/**
 * The raw data columns: the layout above with each field's registry label
 * (the column header, in the grid and the download), description (its
 * tooltip) and default visibility. Registry fields the layout does not list
 * yet come after it, in registry order.
 */
export const rawDataColumns = (fields) => {
  const byKey = new Map((fields ?? []).map((f) => [f.key, f]));
  const known = new Set(RAW_DATA_LAYOUT.map((c) => c.field));
  const layout = [
    ...RAW_DATA_LAYOUT,
    ...(fields ?? []).filter((f) => !known.has(f.key)).map((f) => ({ field: f.key, minWidth: 200 })),
  ];
  return layout
    .filter((c) => byKey.has(c.field))
    .map((c) => {
      const f = byKey.get(c.field);
      return {
        ...c,
        valueFormatter: c.valueFormatter ?? listFormatter,
        headerName: f.label,
        description: f.description,
        defaultHide: Boolean(f.defaultHidden),
      };
    });
};
